import React from "react";

const BASE = "flex w-full min-h-[56px] items-center justify-center rounded-lg font-funnel text-[16px] font-semibold transition-colors duration-150 disabled:cursor-not-allowed";

interface ButtonProps {
  label: string;
  onClick: () => void;
  enabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}

/** Full-width 56px, radius 8, text-primary fill / white text; brand-subtle when disabled. */
export const OnboardingNextButton: React.FC<ButtonProps & { light?: boolean }> = ({
  label,
  onClick,
  enabled = true,
  type = "button",
  light = false,
  className = "",
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={!enabled}
    className={`${BASE} ${
      enabled
        ? light ? "bg-white text-peak-text" : "bg-peak-text text-peak-bg"
        : "bg-peak-brand-subtle text-peak-text-tertiary"
    } ${className}`}
  >
    {label}
  </button>
);

export const OnboardingPrimaryButton: React.FC<ButtonProps> = ({ label, onClick, enabled = true, type = "button", className = "" }) => (
  <button type={type} onClick={onClick} disabled={!enabled} className={`${BASE} bg-peak-text text-peak-bg disabled:opacity-60 ${className}`}>
    {label}
  </button>
);

/** Transparent "secondary" action (Skip for now, I don't have a goal). */
export const OnboardingGlassButton: React.FC<ButtonProps> = ({ label, onClick, enabled = true, type = "button", className = "" }) => (
  <button type={type} onClick={onClick} disabled={!enabled} className={`${BASE} bg-transparent text-peak-text disabled:opacity-60 ${className}`}>
    {label}
  </button>
);

interface MethodButtonProps extends ButtonProps {
  primary?: boolean;
  /** Muted grey fill, no border (social sign-in). */
  muted?: boolean;
  leading?: React.ReactNode;
}

export const OnboardingMethodButton: React.FC<MethodButtonProps> = ({
  label,
  onClick,
  enabled = true,
  primary = false,
  muted = false,
  leading,
  className = "",
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!enabled}
    className={`${BASE} gap-2 disabled:opacity-60 ${
      primary
        ? "bg-peak-text text-peak-bg"
        : muted
          ? "bg-peak-bg-muted text-peak-text"
          : "border border-peak-border bg-peak-bg text-peak-text"
    } ${className}`}
  >
    {leading}
    <span>{label}</span>
  </button>
);

/** 56px circle with a right (or down) arrow. */
export const OnboardingCircleNextButton: React.FC<{ onClick: () => void; light?: boolean; down?: boolean; className?: string }> = ({
  onClick,
  light = false,
  down = false,
  className = "",
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Next"
    className={`flex h-14 w-14 items-center justify-center rounded-full transition-transform active:scale-95 ${
      light ? "bg-white text-peak-navy" : "bg-peak-text text-peak-bg"
    } ${className}`}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={down ? "rotate-90" : ""}>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  </button>
);

export const PeakOrDivider: React.FC = () => (
  <div className="flex items-center gap-3 py-1">
    <span className="h-px flex-1 bg-peak-border" />
    <span className="font-funnel text-[13px] text-peak-text-secondary">Or</span>
    <span className="h-px flex-1 bg-peak-border" />
  </div>
);

export const AppleLogoIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
    <path d="M16.365 1.43c0 1.14-.417 2.2-1.245 3.14-.99 1.12-2.19 1.77-3.49 1.66-.017-.13-.026-.27-.026-.42 0-1.09.47-2.26 1.31-3.21.42-.48.95-.88 1.6-1.2.64-.32 1.25-.49 1.83-.52.02.18.03.36.03.55zM20.7 17.33c-.36.83-.78 1.6-1.27 2.3-.67.96-1.22 1.62-1.65 1.99-.66.6-1.36.91-2.12.93-.54 0-1.2-.15-1.96-.47-.77-.31-1.47-.46-2.12-.46-.68 0-1.4.15-2.18.46-.78.32-1.4.48-1.88.5-.73.03-1.45-.29-2.16-.96-.46-.4-1.04-1.09-1.74-2.07-.75-1.05-1.36-2.27-1.84-3.66C1.27 14.4 1 12.94 1 11.53c0-1.62.35-3.02 1.05-4.19a6.17 6.17 0 0 1 2.2-2.23 5.93 5.93 0 0 1 2.98-.84c.57 0 1.32.18 2.26.52.93.35 1.53.53 1.79.53.2 0 .86-.21 1.99-.62 1.07-.38 1.97-.54 2.71-.48 2.01.16 3.52.95 4.52 2.38-1.8 1.09-2.69 2.61-2.67 4.57.02 1.52.57 2.79 1.65 3.8.49.46 1.03.82 1.64 1.08-.13.38-.27.75-.42 1.1z" />
  </svg>
);

export const GoogleGIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" className={className}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
